import { useEffect, useMemo, useState } from "react";
import SplashCursor from "./components/SplashCursor.jsx";

// --- Content -----------------------------------------------------------------
const PROJECTS = [
  {
    slug: "field-journal-app",
    title: "Field Journal App - Mobile / PWA Design",
    role: "UX/UI Designer",
    year: "2025",
    tags: ["PWA", "Mobile", "Privacy"],
    summary:
      "Designed and optimized a cross-platform field recording app (web, mobile, and PWA) for private audio journaling.",
    cover: "/fieldapp.png",
    problem:
      "Users needed a way to record and organize audio notes in the field with minimal friction. Challenges included iOS PWAs blocking microphone access and playback, navigation shifting on mobile keyboards (esp. Safari), confusion between marketing site and installed app views, and accessibility concerns with toolbar / nav alignment.",
    goals: [
      "Enable reliable audio recording & playback across platforms",
      "Keep all data private, local-only by default",
      "Provide a clean, distraction-free journaling UI (black/white aesthetic)",
      "Ensure nav, toolbar, and onboarding are consistent across mobile, desktop, and iOS PWA",
      "Reduce setup friction — journal entries should be possible in <10 seconds from open",
    ],
    process: [
      "Research: Benchmarked PWA recording restrictions, reviewed iOS limitations, and ran quick usability tests with mobile users",
      "Exploration: Sketches → low-fi wireframes → responsive Figma prototypes",
      "Testing & Iteration: Heuristic review of current build (toolbar, nav, caching issues)",
      "Multiple rounds of QA on iOS Safari, desktop PWA, and Android Chrome",
      "Implementation: Collaborated on service worker updates, manifest adjustments, and responsive CSS fixes",
    ],
    outcomes: [
      "Recording UX: Resolved nav alignment + toolbar issues, enabling smooth record/playback on Android & desktop PWA",
      "iOS handling: Added fallback to device voice memos with upload support until direct PWA audio APIs mature",
      "Onboarding clarity: Reduced confusion between 'marketing page' vs. 'installed app' with clearer entry points",
      "Cross-platform parity: Toolbar and mobile nav now stable across Safari, Chrome, and desktop PWAs",
      "User benefit: Journals open fast, recording is frictionless, and entries stay fully private on device",
    ],
    artifacts: [
      {
        type: "image",
        src: "/fieldapp2.png",
        alt: "Field Journal App interface mockup 2",
        caption: "Additional app interface showing recording and playback features"
      },
      {
        type: "image",
        src: "/fieldapp3.png",
        alt: "Field Journal App interface mockup 3",
        caption: "App interface demonstrating navigation and journal organization"
      }
    ],
    link: "https://fieldjournal.app/",
  },
  {
    slug: "smooth-delivery",
    title: "Aurora San Francisco - Smoothie Delivery App",
    role: "UX/UI Designer",
    year: "2024",
    tags: ["Mobile", "Delivery", "E-commerce"],
    summary: "Designed Aurora SF's mobile delivery app for their premium smoothie service, creating an intuitive ordering experience for health-conscious San Francisco residents.",
    cover: "/aurora.png",
    problem: "Aurora San Francisco needed a mobile app that would not only be visually appealing and easy to navigate, but also provide a seamless and hassle-free user experience from start to finish. The challenge was to design for a diverse range of target audiences with different needs and goals while maintaining Aurora's premium brand positioning.",
    goals: [
      "Design for Aurora SF's diverse user groups: young parents working from home, children, and young professionals",
      "Create user-friendly app with clear instructions and simple navigation that reflects Aurora's premium brand",
      "Ensure accessibility for all users regardless of experience level",
      "Provide convenient ordering experience that fits into busy San Francisco lifestyles",
      "Maintain consistency with Aurora's brand identity while meeting user needs",
    ],
    process: [
      "Research: Analyzed Aurora SF's target audience demographics and psychographics (ages 26-35, San Francisco-based, health-conscious)",
      "User Personas: Developed detailed persona for Marja, a 37-year-old Pilates instructor with busy schedule",
      "Design Strategy: Focused on clear instructions, simple navigation, and visual appeal that aligns with Aurora's premium brand",
      "Accessibility: Conducted accessibility checks during each iteration",
      "Iteration: Based design improvements on usability test feedback from Aurora's target users",
    ],
    outcomes: [
      "Created Aurora SF app that serves diverse user groups with different goals and schedules",
      "Designed solution for busy San Francisco professionals who need quick, healthy breakfast options",
      "Addressed pain points of rushed mornings and lack of time for nutritious meal prep",
      "Ensured accessibility compliance for all Aurora users",
      "Delivered seamless user experience that drives Aurora's business growth in the competitive SF market",
    ],
    artifacts: [
      {
        type: "image",
        src: "/aurora1.png",
        alt: "Aurora smoothie delivery app interface mockup",
        caption: "Main app interface showing smoothie selection and ordering flow"
      },
      {
        type: "image", 
        src: "/aurora4.png",
        alt: "Aurora smoothie delivery app about us page",
        caption: "About Us page showcasing company values and mission"
      }
    ],
    link: "#",
  },
  {
    slug: "website-design",
    title: "Website Design - Esthetitian Services",
    role: "UX/UI Designer",
    year: "2025",
    tags: ["Web Design", "Esthetitian", "Responsive"],
    summary:
      "Designed and developed a modern, responsive website for esthetitian services with clean aesthetics and smooth user interactions.",
    cover: "/kayla1.png",
    problem:
      "This project aims to address the challenge of designing a custom website for an esthetician within 4 pages that effectively showcases her portfolio of work, while seamlessly integrating user-friendly features to facilitate online service bookings and enhance the overall user experience for potential clients.",
    goals: [
      "Design a 4-page website that effectively showcases esthetician portfolio work",
      "Integrate user-friendly online booking features for service appointments",
      "Create an intuitive user experience for potential clients browsing services",
      "Ensure mobile-first responsive design for accessibility across all devices",
      "Maintain professional brand identity that builds trust and credibility"
    ],
    process: [
      "Research: Analyzed competitor portfolio sites and current design trends",
      "Wireframing: Created low-fidelity wireframes for desktop and mobile layouts",
      "Design: Developed high-fidelity mockups with attention to typography and spacing",
      "Prototyping: Built interactive prototypes to test user flows",
      "Development: Collaborated on responsive implementation with smooth animations"
    ],
    outcomes: [
      "Delivered a modern, professional portfolio website",
      "Achieved fast loading times with optimized images and code",
      "Created seamless responsive experience across all device sizes",
      "Implemented smooth scroll animations and micro-interactions",
      "Established strong visual hierarchy and brand consistency"
    ],
    artifacts: [
      {
        type: "image",
        src: "/kayla1.png",
        alt: "Website design screenshot 1",
        caption: "Website design implementation showing key features"
      },
      {
        type: "image",
        src: "/kayla2.png",
        alt: "Website design screenshot 2",
        caption: "Website design showcasing user interface elements"
      },
      {
        type: "image",
        src: "/kayla3.png",
        alt: "Website design screenshot 3",
        caption: "Website design demonstrating responsive layout"
      },
    ],
    link: "#",
  },
  {
    slug: "avandra",
    title: "Avandra - Women's Travel App",
    role: "UX Researcher and Designer",
    year: "2023",
    tags: ["Travel", "Social Platform", "Safety"],
    summary:
      "Designed a platform for solo travelers to connect with each other, exchange information, and find fellow travelers, with a focus on safety and trust for female-identifying travelers.",
    cover: "/avendra3.png",
    problem:
      "More and more people are interested in solo travel, but female-identifying travelers face additional challenges regarding safety and trust. With 84% of solo travelers being women, there's a need for better ways to stay safe, connect, and share information in real time while traveling.",
    goals: [
      "Design a platform for solo travelers to connect and exchange information",
      "Prioritize safety and trust, especially for female-identifying travelers",
      "Provide access to up-to-date destination information",
      "Enable safe social connections without overwhelming onboarding",
      "Balance safety features with accessibility and ease of use"
    ],
    process: [
      "Secondary Research: Analyzed solo travel trends and female traveler safety challenges",
      "Competitive Analysis: Evaluated 6 competitors (NomadHer, Travello, Travel Buddy) for strengths, weaknesses, opportunities, and threats",
      "POV Statement: Defined target user as young financially independent female-identifying solo travelers",
      "Low-fi Wireframes: Explored simple onboarding, user profiles, map-based views, and tip exchange features",
      "Usability Testing: Conducted remote sessions with 5 participants focusing on verification, profile setup, and connection flows",
      "Iteration: Simplified verification flow, balanced safety with accessibility, streamlined navigation for quick connection"
    ],
    outcomes: [
      "Clearer onboarding process increased trust and reduced drop-off rates",
      "Map + profile features gave travelers confidence in connecting with others",
      "Delivered platform concept balancing safety and social interaction for solo travelers",
      "Simplified copy and added visuals based on user feedback about text-heavy flows",
      "Safety steps like ID/email verification increased user trust in the platform"
    ],
    artifacts: [
      {
        type: "image",
        src: "/avendra1.png",
        alt: "Avandra travel app onboarding flow",
        caption: "Onboarding flow guiding users through account setup and verification"
      }
    ],
    link: "#",
  },
];

// --- Utilities ---------------------------------------------------------------
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const isCasePath = (hash) => /^#\/case\//.test(hash);
const slugFromHash = (hash) => hash.replace(/^#\/case\//, "");

// --- Components --------------------------------------------------------------
function Nav() {
  return (
    <nav className="fixed inset-x-0 top-0 z-50 flex items-center justify-between px-6 py-4 bg-black/70 backdrop-blur">
      <a href="#hero" className="text-sm tracking-widest uppercase">Lauren Franklin</a>
      
      {/* Desktop Navigation */}
      <div className="flex gap-6 text-sm">
        <a href="#hero" className="opacity-80 hover:opacity-100">Intro</a>
        <a href="#about" className="opacity-80 hover:opacity-100">About</a>
        <a href="#projects" className="opacity-80 hover:opacity-100">Projects</a>
        <a href="#contact" className="opacity-80 hover:opacity-100">Contact</a>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section id="hero" className="h-screen flex items-center justify-center px-6">
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 max-w-6xl mx-auto">
        {/* Text - full width on mobile, left on desktop */}
        <div className="text-center md:text-left order-1 md:order-2 md:pl-8">
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight font-homoneta">Hello, thanks for stopping by.  <br /><br />
        My name is Lauren and I'm a freelance UX Designer and artist.</h1>
          <p className="mt-6 max-w-xl opacity-80">
            {/* I'm a UX designer focused on simple, human‑centered products. */}
          </p>
        </div>
        
                {/* Star - below text on mobile, left on desktop */}
                <a
                  href="#about"
                  className="w-16 h-16 md:w-24 md:h-24 cursor-pointer transition-all duration-300 hover:rotate-180 hover:scale-110 active:scale-95 flex items-center justify-center flex-shrink-0 order-2 md:order-1"
                  aria-label="Go to About"
                  onClick={(e) => {
                    e.preventDefault();
                    const aboutSection = document.querySelector('#about');
                    if (aboutSection) {
                      aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  <img
                    src="/star.png"
                    alt="Scroll to About"
                    className="w-full h-full object-contain brightness-0 invert animate-spin"
                    style={{ animationDuration: '4s' }}
                  />
                </a>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="min-h-screen flex flex-col items-center justify-center text-center px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-4xl font-bold font-homoneta">Here are some of the research, design and creative projects I worked on this past year..</h2>
        <p className="mt-6 max-w-2xl mx-auto opacity-80 text-sm md:text-base">
          Research → flows → prototypes → tests. <br />
          Below are three recent case studies.
        </p>
      </div>
              <a href="#projects" className="mt-12 w-16 h-16 animate-bounce flex items-center justify-center" aria-label="Go to Projects">
                <img src="/shapes.png" alt="Go to Projects" className="w-full h-full object-contain brightness-0 invert" />
              </a>
    </section>
  );
}

function ProjectsGrid() {
  return (
    <section id="projects" className="min-h-screen px-6 py-24">
      <header className="text-center mb-12">
        <h3 className="text-2xl md:text-4xl font-bold font-homoneta">Projects</h3>
        <p className="mt-3 opacity-70">A collection of UX research, mobile app design, and social platform projects <br />showcasing my design process and outcomes.</p>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {PROJECTS.map((p) => (
          <a
            key={p.slug}
            href={`#/case/${p.slug}`}
            className="group border border-white/15 rounded-2xl p-6 hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 transition"
          >
        <div className={`aspect-video w-full rounded-xl mb-4 flex items-center justify-center overflow-hidden ${p.slug === 'field-journal-app' ? 'bg-white' : p.slug === 'smooth-delivery' ? 'bg-white' : p.slug === 'avandra' ? 'bg-gradient-to-br from-slate-900 to-indigo-900' : 'bg-white/5'}`}>
          {p.cover ? (
            <img
              src={p.cover}
              alt={p.title}
              className={`w-full h-full ${p.slug === 'field-journal-app' || p.slug === 'avandra' ? 'object-contain' : p.slug === 'smooth-delivery' ? 'object-cover' : 'object-cover'}`}
            />
          ) : (
            <span className="opacity-40 text-sm">Add mockup image</span>
          )}
        </div>
            <h4 className="text-lg font-semibold group-hover:underline">{p.title}</h4>
            <p className="mt-2 text-sm opacity-80">{p.summary}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span key={t} className="text-[11px] tracking-wide uppercase border border-white/15 rounded-full px-2 py-1 opacity-80">
                  {t}
                </span>
              ))}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function CaseStudyPage({ slug, onBack, onImageClick }) {
  const project = useMemo(() => PROJECTS.find((p) => p.slug === slug), [slug]);
  if (!project) return (
    <div className="px-6 py-24 max-w-3xl mx-auto">
      <button onClick={onBack} className="underline opacity-80">← Back</button>
      <h1 className="mt-6 text-4xl font-bold">Not found</h1>
    </div>
  );

  return (
    <article className="px-6 py-24 max-w-3xl mx-auto">
      <a href="#projects" onClick={onBack} className="underline opacity-80">←</a>
      <header className="mt-6">
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight font-homoneta">{project.title}</h1>
        <p className="mt-2 opacity-80">{project.role} · {project.year}</p>
      </header>

      <section className="mt-10">
        <h2 className="text-xl font-semibold font-homoneta">Overview</h2>
        <p className="mt-3 opacity-90">{project.summary}</p>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold font-homoneta">Problem</h2>
        <p className="mt-3 opacity-90">{project.problem}</p>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold font-homoneta">Goals</h2>
        <ul className="mt-3 list-disc list-inside space-y-2 opacity-90">
          {project.goals.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
        
        {/* Show primary feed image for Avandra project under goals */}
        {project.slug === 'avandra' && (
          <div className="mt-6">
            <div 
              className="project-image-container rounded-xl flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90 transition-opacity aspect-video bg-black"
              onClick={() => onImageClick("/avendra2.png", "Avandra travel app primary feed", "Primary feed experience showing travel connections and social features")}
            >
              <img
                src="/avendra2.png"
                alt="Avandra travel app primary feed"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="mt-3 text-sm opacity-70 text-center">Primary feed experience showing travel connections and social features</p>
          </div>
        )}
        
        {/* Show user flow image for Aurora project under goals */}
        {project.slug === 'smooth-delivery' && (
          <div className="mt-6">
            <div 
              className="project-image-container rounded-xl flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90 transition-opacity aspect-video bg-white"
              onClick={() => onImageClick("/auroraflow.png", "Aurora smoothie delivery app flow diagram", "Complete user experience flow and interaction patterns")}
            >
              <img
                src="/auroraflow.png"
                alt="Aurora smoothie delivery app flow diagram"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="mt-3 text-sm opacity-70 text-center">Complete user experience flow and interaction patterns</p>
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold font-homoneta">Process</h2>
        <ol className="mt-3 list-decimal list-inside space-y-2 opacity-90">
          {project.process.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold font-homoneta">Outcomes</h2>
        <ul className="mt-3 list-disc list-inside space-y-2 opacity-90">
          {project.outcomes.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      </section>

      {project.link && project.link !== "#" && (
        <section className="mt-10">
          <h2 className="text-xl font-semibold font-homoneta">Live Project</h2>
          <div className="mt-3">
            <a 
              href={project.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
            >
              View Live Site
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </section>
      )}

      <section className="mt-10">
          <h2 className="text-xl font-semibold font-homoneta">Artifacts</h2>
        <div className={`mt-4 grid gap-4 ${project.slug === 'field-journal-app' || project.slug === 'website-design' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {project.artifacts ? project.artifacts.map((artifact, i) => (
            <div key={i} className={`space-y-2 ${project.slug === 'field-journal-app' && i === 0 ? 'col-span-1' : ''}`}>
              <div 
                className={`project-image-container rounded-xl flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${
                  project.slug === 'field-journal-app' && i === 0 ? 'aspect-[4/3] bg-black' : 
                  project.slug === 'website-design' ? 'aspect-video bg-white' :
                  project.slug === 'avandra' ? 'aspect-video bg-black' : 
                  'aspect-video bg-white'
                }`}
                onClick={() => onImageClick(artifact.src, artifact.alt, artifact.caption)}
              >
                <img
                  src={artifact.src}
                  alt={artifact.alt}
                  className="w-full h-full object-contain"
                />
              </div>
              {artifact.caption && (
                <p className="text-sm opacity-70 text-center">{artifact.caption}</p>
              )}
            </div>
          )) : [1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-video rounded-xl bg-white/5 flex items-center justify-center">
              <span className="opacity-40 text-sm">Add image</span>
            </div>
          ))}
        </div>
      </section>

    </article>
  );
}

function Contact() {
  return (
    <section id="contact" className="min-h-screen flex flex-col items-center justify-center text-center px-6">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl md:text-4xl font-bold font-homoneta mb-6">Get in Touch</h2>
        <p className="text-lg opacity-80 mb-8">
          Interested in working together or have questions about my work? 
          I'd love to hear from you.
        </p>
        <div className="space-y-4">
          <a 
            href="mailto:laurenfranklin@protonmail.com" 
            className="inline-block bg-white text-black px-8 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
          >
            Send Email
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-6 py-10 border-t border-white/10 text-sm opacity-70 text-center">
      <p>© {new Date().getFullYear()} Lauren Franklin - <a className="underline" href="#contact">Contact</a> · <a className="underline" href="https://laurenfranklin.notion.site/Lauren-Franklin-dc19e52617e843c6a35a1d450c1dca5d?source=copy_link" target="_blank" rel="noopener noreferrer">CV</a></p>
    </footer>
  );
}

function LoginModal({ onLogin, password, setPassword, showError }) {
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md w-full mx-4 border border-white/20">
        <h2 className="text-2xl font-bold text-center mb-6 font-homoneta">Enter Password</h2>
        <form onSubmit={onLogin} className="space-y-4">
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent"
              autoFocus
            />
            {showError && (
              <p className="mt-2 text-red-400 text-sm">Incorrect password. Please try again.</p>
            )}
          </div>
          <button
            type="submit"
            className="w-full py-3 bg-white text-black rounded-lg font-semibold hover:bg-white/90 transition-colors"
          >
            Access Portfolio
          </button>
        </form>
      </div>
    </div>
  );
}

// --- Image Modal Component --------------------------------------------------
function ImageModal({ isOpen, onClose, imageSrc, imageAlt, caption }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="image-modal-backdrop fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-6xl max-h-[90vh] w-full">
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/70 hover:text-white text-3xl font-bold z-10 transition-colors"
          aria-label="Close modal"
        >
          ×
        </button>
        <div className="image-modal-content bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {caption && (
            <p className="mt-4 text-center text-white/80 text-sm">{caption}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- App ---------------------------------------------------------------------
export default function App() {
  const [hash, setHash] = useState(typeof window !== "undefined" ? window.location.hash : "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showError, setShowError] = useState(false);
  const [modalImage, setModalImage] = useState({ isOpen: false, src: "", alt: "", caption: "" });
  
  const openImageModal = (src, alt, caption) => {
    setModalImage({ isOpen: true, src, alt, caption });
  };

  const closeImageModal = () => {
    setModalImage({ isOpen: false, src: "", alt: "", caption: "" });
  };
  
  const onBack = () => {
    window.location.hash = "#projects";
    // Scroll to projects section after hash change
    setTimeout(() => {
      const projectsSection = document.querySelector('#projects');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === "0000") {
      setIsAuthenticated(true);
      setShowError(false);
    } else {
      setShowError(true);
    }
  };

  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Smooth scroll for in‑page anchors
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (href?.startsWith('#/') ) return; // route change
      const el = document.querySelector(href);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', href);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const caseMode = isCasePath(hash);
  const slug = caseMode ? slugFromHash(hash) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [caseMode, slug]);


  if (!isAuthenticated) {
    return (
      <div className="bg-black text-white min-h-screen font-[system-ui,Segoe UI,Roboto,Inter,sans-serif]">
        <Nav />
        <LoginModal 
          onLogin={handleLogin}
          password={password}
          setPassword={setPassword}
          showError={showError}
        />
        
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen font-[system-ui,Segoe UI,Roboto,Inter,sans-serif]">
      <SplashCursor color="#ffffff" size={20} />
      
      <a
        href="#projects"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-1 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded"
      >
        Skip to projects
      </a>

      <Nav />

      {!caseMode ? (
        <main>
          <Hero />
          <About />
          <ProjectsGrid />
          <Contact />
        </main>
      ) : (
        <main>
          <CaseStudyPage slug={slug} onBack={onBack} onImageClick={openImageModal} />
        </main>
      )}

      <Footer />
      
      <ImageModal
        isOpen={modalImage.isOpen}
        onClose={closeImageModal}
        imageSrc={modalImage.src}
        imageAlt={modalImage.alt}
        caption={modalImage.caption}
      />
      
    </div>
  );
}
